import { theme } from 'antd';
import type { GlobalToken } from 'antd';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, SlidersHorizontalIcon } from '@phosphor-icons/react';
import styled from 'styled-components';

import { type PlaybookGradient, resolveBackground } from './gradients';
import { radius } from '../../theme/radius';

export type { PlaybookGradient } from './gradients';

export type PlaybookCreationCardProps = {
  owner?: string;
  title: string;
  subtitle?: string;
  gradient?: PlaybookGradient | (string & {});
  selected: boolean;
  onSelect: () => void;
  /** Unselected cards show a Preview link; selected cards show Customize. */
  onPreview: () => void;
  onCustomize: () => void;
  /** Optional ⋮ menu, pinned over the top-right of the thumbnail. The card
   *  owns only the placement — what the menu looks like and does is the
   *  consumer's (mirrors `PlaybookCard`'s slot). */
  menu?: ReactNode;
  className?: string;
};

/**
 * Playbook card for the experience-creation carousel. Distinct from
 * `PlaybookCard` (used on the playbooks management page): this variant owns the
 * creation-specific selection UI — a checkmark-circle select that fills with the
 * brand color when selected, and a Customize pill (Preview when unselected).
 */
export const PlaybookCreationCard = ({
  owner,
  title,
  subtitle,
  gradient,
  selected,
  onSelect,
  onPreview,
  onCustomize,
  menu,
  className,
}: PlaybookCreationCardProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { token } = theme.useToken();

  return (
    <StyledCard className={className} $token={token} $selected={selected}>
      {/* `data-playbook-menu` marks the slot the way `PlaybookCard` does, where
          a card-level click uses it to ignore clicks on the menu. This card has
          no such handler — only its Check and its pill act — so the attribute
          does nothing here yet. It stays because adding one later is the likely
          change, and the guard it looks for would already be in place. */}
      {menu && <MenuSlot data-playbook-menu>{menu}</MenuSlot>}
      <div
        style={{
          height: 200,
          flexShrink: 0,
          margin: 9,
          borderRadius: radius.box,
          background: resolveBackground(gradient),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Body>
        {owner && <Owner $token={token}>{owner}</Owner>}
        <Name $token={token}>{title}</Name>
        {subtitle && <Desc $token={token}>{subtitle}</Desc>}

        <StyledDivider $token={token} />
        <Actions>
          {selected ? (
            <CustomizePill $token={token} type="button" onClick={onCustomize}>
              <SlidersHorizontalIcon size={16} weight="bold" />
              {t('components.playbook.customize')}
            </CustomizePill>
          ) : (
            <PreviewLink $token={token} type="button" onClick={onPreview}>
              {t('components.playbook.preview')}
            </PreviewLink>
          )}
          <CheckCircle
            $token={token}
            $selected={selected}
            type="button"
            aria-pressed={selected}
            aria-label={t('components.playbook.select')}
            onClick={onSelect}
          >
            <CheckIcon size={22} weight="bold" />
          </CheckCircle>
        </Actions>
      </Body>
    </StyledCard>
  );
};

export default PlaybookCreationCard;

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

const StyledCard = styled.div<TokenProps & { $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: ${radius.box}px;
  overflow: hidden;
  background: ${({ $token }) => $token.colorBgContainer};
  border: 2px solid
    ${({ $token, $selected }) =>
      $selected ? $token.colorPrimaryActive : 'transparent'};
  box-shadow: ${({ $token }) => $token.boxShadowTertiary};
  transition: border-color ${({ $token }) => $token.motionDurationMid}
    ${({ $token }) => $token.motionEaseInOut};

  &:hover {
    border-color: ${({ $token, $selected }) =>
      $selected ? $token.colorPrimaryActive : $token.colorBorder};
  }
`;

// Sits 9px inside the thumbnail (which has its own 9px margin), matching the
// design mock. Only the position lives here — the trigger's own chrome comes
// from whatever the consumer passes in.
const MenuSlot = styled.div`
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 1;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 8px 18px 0;
`;

const Owner = styled.div<TokenProps>`
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token }) => $token.colorTextTertiary};
  margin-bottom: 4px;
`;

const Name = styled.div<TokenProps>`
  font-size: ${({ $token }) => $token.fontSizeHeading4}px;
  font-weight: 600;
  letter-spacing: -0.04em;
  line-height: 1.2;
  color: ${({ $token }) => $token.colorText};
  margin-bottom: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Desc = styled.p<TokenProps>`
  font-size: ${({ $token }) => $token.fontSize}px;
  color: ${({ $token }) => $token.colorText};
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Auto top margin keeps the divider + footer a consistent distance from the
// card bottom, with or without a subtitle.
const StyledDivider = styled.div<TokenProps>`
  height: 1px;
  margin: auto 0 15px;
  background: ${({ $token }) => $token.colorBorderSecondary};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
`;

const PreviewLink = styled.button<TokenProps>`
  border: none;
  background: none;
  padding: 4px 2px;
  font-family: inherit;
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 600;
  color: ${({ $token }) => $token.colorTextSecondary};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color ${({ $token }) => $token.motionDurationFast};

  &:hover {
    color: ${({ $token }) => $token.colorPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const CustomizePill = styled.button<TokenProps>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 44px;
  padding: 0 18px;
  border-radius: ${radius.pill}px;
  font-family: inherit;
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid ${({ $token }) => $token.colorPrimary};
  background: ${({ $token }) => $token.colorPrimaryBg};
  color: ${({ $token }) => $token.colorPrimary};
  transition: all ${({ $token }) => $token.motionDurationFast};

  &:hover {
    border-color: ${({ $token }) => $token.colorPrimaryHover};
    color: ${({ $token }) => $token.colorPrimaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
  }
`;

const CheckCircle = styled.button<TokenProps & { $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  padding: 0;
  border-radius: ${radius.circle};
  cursor: pointer;
  border: 1px solid
    ${({ $token, $selected }) =>
      $selected ? $token.colorPrimaryActive : $token.colorBorder};
  background: ${({ $token, $selected }) =>
    $selected ? $token.colorPrimaryActive : $token.colorBgContainer};
  color: ${({ $token, $selected }) =>
    $selected ? $token.colorWhite : $token.colorTextTertiary};
  transition: all ${({ $token }) => $token.motionDurationMid};

  &:hover {
    border-color: ${({ $token, $selected }) =>
      $selected ? $token.colorPrimaryActive : $token.colorPrimary};
    color: ${({ $token, $selected }) =>
      $selected ? $token.colorWhite : $token.colorPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
  }
`;
